import type { LOCALE_CODE } from '~~/lib/ContentfulService';
import { useRouteParamLocale } from '~/composables/useLocale';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import { buildActiveLocale } from '~/utils/locale/buildActiveLocale';
import type { ActiveLocale } from '~/utils/locale/ActiveLocale';

type ResolvedLocaleResponse = { code: LOCALE_CODE; default: boolean };

/**
 * Guards against a stale CDN-cached response still using the pre-refactor
 * bare-string shape for `/api/contentful/resolveLocale` (that route is
 * cached for 30 days with no flush-on-deploy). Without this check, such a
 * response would reach `buildActiveLocale()` and throw inside
 * `convertContentfulToWebappLocaleCode` (`.toLowerCase()` on a non-string).
 */
function isResolvedLocaleResponse(
  value: unknown
): value is ResolvedLocaleResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ResolvedLocaleResponse).code === 'string' &&
    typeof (value as ResolvedLocaleResponse).default === 'boolean'
  );
}

export default defineNuxtPlugin({
  name: 'locale',
  enforce: 'pre',
  async setup() {
    const requestedLocale = useRouteParamLocale();
    const resolvedLocale = useState<LOCALE_CODE | undefined>(
      'resolvedLocale',
      () => undefined
    );
    const activeLocale = useState<ActiveLocale>('activeLocale');
    const availableContentfulLocaleCodes = useState<string[]>(
      'locales',
      () => []
    );
    const availableWebAppLocales = useState<string[]>(
      'localesMapped',
      () => []
    );

    if (!resolvedLocale.value) {
      try {
        const fetchedLocaleRaw = await $fetch<unknown>(
          '/api/contentful/resolveLocale',
          {
            ...DEFAULT_FETCH_OPTIONS,
            query: { locale: requestedLocale.value }
          }
        );

        const fetchedLocale = isResolvedLocaleResponse(fetchedLocaleRaw)
          ? fetchedLocaleRaw
          : undefined;

        resolvedLocale.value = fetchedLocale?.code;

        if (fetchedLocale) {
          activeLocale.value = buildActiveLocale(fetchedLocale);
        }
      } catch (_e) {
        // XXX: handle error?
      }
    }

    if (!activeLocale.value) {
      // Reached only when resolution failed entirely (network error, a 404
      // from the endpoint, or an unexpected/stale response shape) — 'en' is
      // an emergency display fallback here, not a confirmed match against
      // the actual Contentful default locale, so isDefault must be false.
      activeLocale.value = buildActiveLocale({
        code: resolvedLocale.value ?? 'en',
        default: false
      });
    }

    return {
      provide: {
        /**
         * The **mapped** requested locale!
         * @example "zh-tw"
         */
        requestedLocale,
        /**
         * A list of all available locale *codes*
         * @example ["de-DE", "en", "en-US"]
         */
        availableContentfulLocaleCodes,

        /**
         * A list of all available webapp locales
         * @example ["de-de", "en", "en-us"]
         */
        availableWebAppLocales,

        /**
         * The canonical, correct representation of the active locale,
         * unifying the webapp form and the true-cased Contentful code.
         */
        activeLocale
      }
    };
  }
});
